import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { codeRepoAPI, assignmentAPI, groupAPI, authAPI } from '../services/api';

const CodeRepoBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [repo, setRepo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('assignments');
  
  // Assignments tab state
  const [allAssignments, setAllAssignments] = useState([]);
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [repoAssignments, setRepoAssignments] = useState([]);
  
  // Assignment tab state
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [assignedStudentsInfo, setAssignedStudentsInfo] = useState(null);

  useEffect(() => {
    fetchRepo();
    fetchAllAssignments();
    fetchGroups();
    fetchStudents();
  }, [id]);

  useEffect(() => {
    filterAssignments();
  }, [allAssignments, assignmentSearch, repoAssignments]);

  useEffect(() => {
    filterStudents();
  }, [students, studentSearch]);

  const fetchRepo = async () => {
    try {
      setLoading(true);
      const response = await codeRepoAPI.getRepoById(id);
      setRepo(response.data);
      setRepoAssignments(response.data.assignments || []);
      
      // Fetch assigned students info
      const studentsInfo = await codeRepoAPI.getAssignedStudents(id);
      setAssignedStudentsInfo(studentsInfo.data);
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching code repo');
      console.error('Error fetching repo:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAssignments = async () => {
    try {
      const response = await assignmentAPI.getAllAssignments({ limit: 1000 });
      setAllAssignments(response.data.assignments || []);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      setGroups(response.data.groups || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await authAPI.getAllStudents(1, 1000); // Get all students for assignment
      setStudents(response.data.students || []);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  const filterAssignments = () => {
    const repoAssignmentIds = repoAssignments.map(a => a._id);
    let filtered = allAssignments.filter(a => !repoAssignmentIds.includes(a._id));

    if (assignmentSearch) {
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(assignmentSearch.toLowerCase()) ||
        a.description?.toLowerCase().includes(assignmentSearch.toLowerCase())
      );
    }

    setFilteredAssignments(filtered);
  };

  const filterStudents = () => {
    if (!studentSearch) {
      setFilteredStudents(students);
      return;
    }

    const filtered = students.filter(s =>
      s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
      s.email.toLowerCase().includes(studentSearch.toLowerCase())
    );
    setFilteredStudents(filtered);
  };

  const handleAddAssignments = async () => {
    if (selectedAssignments.length === 0) {
      setError('Please select at least one assignment');
      return;
    }

    try {
      await codeRepoAPI.addAssignments(id, selectedAssignments);
      setSuccess('Assignments added successfully');
      setTimeout(() => setSuccess(null), 3000);
      setSelectedAssignments([]);
      fetchRepo();
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding assignments');
      console.error('Error adding assignments:', err);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await codeRepoAPI.removeAssignment(id, assignmentId);
      setSuccess('Assignment removed successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchRepo();
    } catch (err) {
      setError(err.response?.data?.message || 'Error removing assignment');
      console.error('Error removing assignment:', err);
    }
  };

  const handleMoveAssignment = async (index, direction) => {
    const newAssignments = [...repoAssignments];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newAssignments.length) return;

    [newAssignments[index], newAssignments[newIndex]] = [newAssignments[newIndex], newAssignments[index]];

    try {
      await codeRepoAPI.reorderAssignments(id, newAssignments.map(a => a._id));
      setRepoAssignments(newAssignments);
      setSuccess('Order updated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error reordering assignments');
      console.error('Error reordering:', err);
      fetchRepo(); // Revert on error
    }
  };

  const handleAssignGroups = async () => {
    if (selectedGroups.length === 0) {
      setError('Please select at least one group');
      return;
    }

    try {
      await codeRepoAPI.assignToGroups(id, selectedGroups);
      setSuccess('Groups assigned successfully');
      setTimeout(() => setSuccess(null), 3000);
      setSelectedGroups([]); // Clear selection after assignment
      fetchRepo();
    } catch (err) {
      setError(err.response?.data?.message || 'Error assigning groups');
      console.error('Error assigning groups:', err);
    }
  };

  const handleRemoveGroup = async (groupId) => {
    try {
      await codeRepoAPI.removeFromGroup(id, groupId);
      setSuccess('Group removed successfully');
      setTimeout(() => setSuccess(null), 3000);
      setSelectedGroups(prev => prev.filter(gId => gId !== groupId));
      fetchRepo();
    } catch (err) {
      setError(err.response?.data?.message || 'Error removing group');
      console.error('Error removing group:', err);
    }
  };

  const handleAssignStudents = async () => {
    if (selectedStudents.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      await codeRepoAPI.assignToStudents(id, selectedStudents);
      setSuccess('Students assigned successfully');
      setTimeout(() => setSuccess(null), 3000);
      setSelectedStudents([]); // Clear selection after assignment
      fetchRepo();
    } catch (err) {
      setError(err.response?.data?.message || 'Error assigning students');
      console.error('Error assigning students:', err);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await codeRepoAPI.removeStudent(id, studentId);
      setSuccess('Student removed successfully');
      setTimeout(() => setSuccess(null), 3000);
      setSelectedStudents(prev => prev.filter(sId => sId !== studentId));
      fetchRepo();
    } catch (err) {
      setError(err.response?.data?.message || 'Error removing student');
      console.error('Error removing student:', err);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading" style={{ textAlign: 'center', padding: '48px 24px', color: '#718096' }}>
          Loading repository...
        </div>
      </div>
    );
  }

  if (!repo) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <h2 style={{ color: '#2d3748', fontSize: '20px', marginBottom: '16px' }}>Code Repository not found</h2>
          <button
            onClick={() => navigate('/admin/code-repos')}
            className="btn btn-primary"
            style={{ fontSize: '13px' }}
          >
            ← Back to Code Repos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate('/admin/code-repos')}
            className="btn btn-secondary"
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back to Repositories
          </button>
          
          <div style={{ marginBottom: '8px' }}>
            <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
              ⚙️ {repo.title} Builder
            </h2>
            <p style={{ color: '#718096', fontSize: '13px' }}>
              Manage assignments, groups, and student access
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', marginTop: '12px' }}>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#e0e7ff',
              color: '#667eea',
              borderRadius: '6px',
              fontWeight: '500',
              fontSize: '12px'
            }}>
              📋 {repo.repoCode}
            </span>
            <span style={{ color: '#cbd5e0' }}>•</span>
            <span style={{ color: '#4a5568', fontWeight: '500' }}>
              {repoAssignments.length} Assignment{repoAssignments.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '13px'
          }}>
            {success}
          </div>
        )}

        {/* Tabs */}
        <div style={{ marginBottom: '24px', borderBottom: '2px solid #e2e8f0' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setActiveTab('assignments')}
              style={{
                padding: '12px 20px',
                backgroundColor: activeTab === 'assignments' ? '#667eea' : 'transparent',
                color: activeTab === 'assignments' ? 'white' : '#718096',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                transition: 'all 0.2s',
                borderBottom: activeTab === 'assignments' ? 'none' : '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'assignments') {
                  e.target.style.backgroundColor = '#f7fafc';
                  e.target.style.color = '#667eea';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'assignments') {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#718096';
                }
              }}
            >
              📝 Manage Assignments
            </button>
            <button
              onClick={() => setActiveTab('assignment')}
              style={{
                padding: '12px 20px',
                backgroundColor: activeTab === 'assignment' ? '#667eea' : 'transparent',
                color: activeTab === 'assignment' ? 'white' : '#718096',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                transition: 'all 0.2s',
                borderBottom: activeTab === 'assignment' ? 'none' : '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'assignment') {
                  e.target.style.backgroundColor = '#f7fafc';
                  e.target.style.color = '#667eea';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'assignment') {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#718096';
                }
              }}
            >
              👥 Assign Access
            </button>
          </div>
        </div>

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Available Assignments */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fafafa',
              border: '1.5px solid #e2e8f0',
              borderRadius: '12px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
                📚 Available Assignments
              </h3>
              
              <input
                type="text"
                placeholder="🔍 Search assignments..."
                value={assignmentSearch}
                onChange={(e) => setAssignmentSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />

              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px',
                backgroundColor: 'white'
              }}>
                {filteredAssignments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>📝</div>
                    <p style={{ color: '#718096', fontSize: '13px' }}>
                      {assignmentSearch ? 'No assignments found' : 'All assignments are already in this repo'}
                    </p>
                  </div>
                ) : (
                  filteredAssignments.map(assignment => (
                    <div
                      key={assignment._id}
                      onClick={() => {
                        if (selectedAssignments.includes(assignment._id)) {
                          setSelectedAssignments(prev => prev.filter(id => id !== assignment._id));
                        } else {
                          setSelectedAssignments(prev => [...prev, assignment._id]);
                        }
                      }}
                      style={{
                        padding: '12px',
                        marginBottom: '8px',
                        backgroundColor: selectedAssignments.includes(assignment._id) ? '#e0e7ff' : 'white',
                        border: selectedAssignments.includes(assignment._id) ? '2px solid #667eea' : '1.5px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedAssignments.includes(assignment._id)) {
                          e.currentTarget.style.backgroundColor = '#f7fafc';
                          e.currentTarget.style.borderColor = '#cbd5e0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedAssignments.includes(assignment._id)) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: '#2d3748' }}>
                        {assignment.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{
                          padding: '2px 6px',
                          backgroundColor: '#f0fdf4',
                          color: '#166534',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          {assignment.level}
                        </span>
                        <span>•</span>
                        <span>{assignment.points || 0} pts</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <button
                onClick={handleAddAssignments}
                disabled={selectedAssignments.length === 0}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  padding: '10px',
                  backgroundColor: selectedAssignments.length === 0 ? '#e2e8f0' : '#667eea',
                  color: selectedAssignments.length === 0 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedAssignments.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (selectedAssignments.length > 0) {
                    e.target.style.backgroundColor = '#5568d3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedAssignments.length > 0) {
                    e.target.style.backgroundColor = '#667eea';
                  }
                }}
              >
                ➕ Add Selected ({selectedAssignments.length})
              </button>
            </div>

            {/* Repo Assignments */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fafafa',
              border: '1.5px solid #e2e8f0',
              borderRadius: '12px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
                📋 Repository Assignments ({repoAssignments.length})
              </h3>

              <div style={{
                maxHeight: '520px',
                overflowY: 'auto',
                backgroundColor: 'white',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px'
              }}>
                {repoAssignments.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 16px' }}>
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>📦</div>
                    <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                      No assignments in this repository yet
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: '12px' }}>
                      Select assignments from the left to add them
                    </p>
                  </div>
                ) : (
                  repoAssignments.map((assignment, index) => (
                    <div
                      key={assignment._id}
                      style={{
                        padding: '14px',
                        marginBottom: '10px',
                        backgroundColor: 'white',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.1)';
                        e.currentTarget.style.borderColor = '#cbd5e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px', color: '#2d3748' }}>
                            <span style={{
                              display: 'inline-block',
                              width: '24px',
                              height: '24px',
                              lineHeight: '24px',
                              textAlign: 'center',
                              backgroundColor: '#667eea',
                              color: 'white',
                              borderRadius: '6px',
                              marginRight: '10px',
                              fontSize: '12px',
                              fontWeight: '700'
                            }}>
                              {index + 1}
                            </span>
                            {assignment.title}
                          </div>
                          <div style={{ fontSize: '12px', color: '#718096', marginLeft: '34px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{
                              padding: '2px 6px',
                              backgroundColor: '#f0fdf4',
                              color: '#166534',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              {assignment.level}
                            </span>
                            <span>•</span>
                            <span>{assignment.points || 0} pts</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => handleMoveAssignment(index, 'up')}
                            disabled={index === 0}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: index === 0 ? '#f7fafc' : '#e0e7ff',
                              color: index === 0 ? '#cbd5e0' : '#667eea',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: index === 0 ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: '700',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (index !== 0) {
                                e.target.style.backgroundColor = '#667eea';
                                e.target.style.color = 'white';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (index !== 0) {
                                e.target.style.backgroundColor = '#e0e7ff';
                                e.target.style.color = '#667eea';
                              }
                            }}
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => handleMoveAssignment(index, 'down')}
                            disabled={index === repoAssignments.length - 1}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: index === repoAssignments.length - 1 ? '#f7fafc' : '#e0e7ff',
                              color: index === repoAssignments.length - 1 ? '#cbd5e0' : '#667eea',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: index === repoAssignments.length - 1 ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              fontWeight: '700',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (index !== repoAssignments.length - 1) {
                                e.target.style.backgroundColor = '#667eea';
                                e.target.style.color = 'white';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (index !== repoAssignments.length - 1) {
                                e.target.style.backgroundColor = '#e0e7ff';
                                e.target.style.color = '#667eea';
                              }
                            }}
                          >
                            ↓
                          </button>
                          <button
                            onClick={() => handleRemoveAssignment(assignment._id)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#fee2e2',
                              color: '#991b1b',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '700',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#fca5a5';
                              e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#fee2e2';
                              e.target.style.color = '#991b1b';
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Tab */}
      {activeTab === 'assignment' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Groups */}
          <div style={{
            padding: '20px',
            backgroundColor: '#fafafa',
            border: '1.5px solid #e2e8f0',
            borderRadius: '12px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
              👥 Assign to Groups
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontSize: '13px', 
                fontWeight: '600',
                color: '#4a5568'
              }}>
                Select Groups
              </label>
              <div style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px',
                maxHeight: '220px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                {groups.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>👥</div>
                    <p style={{ color: '#718096', fontSize: '13px' }}>
                      No groups available
                    </p>
                  </div>
                ) : (
                  groups.map(group => (
                    <label
                      key={group._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'all 0.2s',
                        backgroundColor: selectedGroups.includes(group._id) ? '#e0e7ff' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedGroups.includes(group._id)) {
                          e.currentTarget.style.backgroundColor = '#f7fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedGroups.includes(group._id)) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(group._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedGroups(prev => [...prev, group._id]);
                          } else {
                            setSelectedGroups(prev => prev.filter(id => id !== group._id));
                          }
                        }}
                        style={{ 
                          marginRight: '12px', 
                          width: '18px', 
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#667eea'
                        }}
                      />
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#2d3748' }}>
                        {group.name}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <button
              onClick={handleAssignGroups}
              disabled={selectedGroups.length === 0}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: selectedGroups.length === 0 ? '#e2e8f0' : '#667eea',
                color: selectedGroups.length === 0 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: selectedGroups.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                marginBottom: '20px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (selectedGroups.length > 0) {
                  e.target.style.backgroundColor = '#5568d3';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedGroups.length > 0) {
                  e.target.style.backgroundColor = '#667eea';
                }
              }}
            >
              ✓ Assign Selected Groups ({selectedGroups.length})
            </button>

            {/* Current Groups */}
            <div>
              <h4 style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                marginBottom: '12px',
                color: '#4a5568'
              }}>
                Currently Assigned Groups ({repo.assignedGroups?.length || 0})
              </h4>
              <div style={{ 
                maxHeight: '220px', 
                overflowY: 'auto',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                padding: '10px',
                backgroundColor: 'white'
              }}>
                {repo.assignedGroups && repo.assignedGroups.length > 0 ? (
                  repo.assignedGroups.map(group => (
                    <div
                      key={group._id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: '#f7fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e0e7ff';
                        e.currentTarget.style.borderColor = '#cbd5e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#2d3748' }}>
                        {group.name}
                      </span>
                      <button
                        onClick={() => handleRemoveGroup(group._id)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fca5a5';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fee2e2';
                          e.target.style.color = '#991b1b';
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px 16px' }}>
                    <p style={{ color: '#9ca3af', fontSize: '12px' }}>
                      No groups assigned yet
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Students */}
          <div style={{
            padding: '20px',
            backgroundColor: '#fafafa',
            border: '1.5px solid #e2e8f0',
            borderRadius: '12px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
              🎓 Assign to Students
            </h3>

            <input
              type="text"
              placeholder="🔍 Search students..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '13px',
                outline: 'none',
                transition: 'all 0.2s',
                backgroundColor: 'white'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />

            <div style={{
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px',
              maxHeight: '220px',
              overflowY: 'auto',
              backgroundColor: 'white',
              marginBottom: '16px'
            }}>
              {filteredStudents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔍</div>
                  <p style={{ color: '#718096', fontSize: '13px' }}>
                    {studentSearch ? 'No students found' : 'No students available'}
                  </p>
                </div>
              ) : (
                filteredStudents.map(student => (
                  <label
                    key={student._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      transition: 'all 0.2s',
                      backgroundColor: selectedStudents.includes(student._id) ? '#e0e7ff' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedStudents.includes(student._id)) {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedStudents.includes(student._id)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStudents(prev => [...prev, student._id]);
                        } else {
                          setSelectedStudents(prev => prev.filter(id => id !== student._id));
                        }
                      }}
                      style={{ 
                        marginRight: '12px', 
                        width: '18px', 
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#667eea'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#2d3748' }}>
                        {student.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {student.email}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>

            <button
              onClick={handleAssignStudents}
              disabled={selectedStudents.length === 0}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: selectedStudents.length === 0 ? '#e2e8f0' : '#667eea',
                color: selectedStudents.length === 0 ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: selectedStudents.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '13px',
                marginBottom: '20px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (selectedStudents.length > 0) {
                  e.target.style.backgroundColor = '#5568d3';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedStudents.length > 0) {
                  e.target.style.backgroundColor = '#667eea';
                }
              }}
            >
              ✓ Assign Selected Students ({selectedStudents.length})
            </button>

            {/* Current Students */}
            <div>
              <h4 style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                marginBottom: '12px',
                color: '#4a5568'
              }}>
                Directly Assigned Students ({repo.assignedStudents?.length || 0})
              </h4>
              <div style={{ 
                maxHeight: '220px', 
                overflowY: 'auto',
                border: '1.5px solid #e2e8f0',
                borderRadius: '8px',
                padding: '10px',
                backgroundColor: 'white'
              }}>
                {repo.assignedStudents && repo.assignedStudents.length > 0 ? (
                  repo.assignedStudents.map(student => (
                    <div
                      key={student._id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        backgroundColor: '#f7fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e0e7ff';
                        e.currentTarget.style.borderColor = '#cbd5e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f7fafc';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#2d3748' }}>
                          {student.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#718096' }}>
                          {student.email}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveStudent(student._id)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#fee2e2',
                          color: '#991b1b',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fca5a5';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fee2e2';
                          e.target.style.color = '#991b1b';
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px 16px' }}>
                    <p style={{ color: '#9ca3af', fontSize: '12px' }}>
                      No students directly assigned yet
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Total students summary */}
            {assignedStudentsInfo && (
              <div style={{
                marginTop: '16px',
                padding: '14px',
                backgroundColor: '#eff6ff',
                border: '1.5px solid #bfdbfe',
                borderRadius: '8px'
              }}>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: '600', 
                  marginBottom: '6px',
                  color: '#1e40af'
                }}>
                  📊 Total Students with Access: {assignedStudentsInfo.allStudents?.length || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#60a5fa' }}>
                  {assignedStudentsInfo.directStudents?.length || 0} direct + {assignedStudentsInfo.groupStudents?.length || 0} from groups
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CodeRepoBuilder;
